import { FunctionComponent, useCallback } from 'react';
import Head from 'next/head';
import { H1, H2, H4, Text } from 'components/Typography/Typography';
import { Layout, LayoutWrapper } from 'components/Layout/Layout';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import { Footer } from 'components/Layout/Footer';
import { Header } from 'components/Layout/Header';

const CopyrightPolicy: FunctionComponent = () => {
  const router = useRouter();

  const onRoute = useCallback(
    (path) => {
      router.push(path);
    },
    [router],
  );

  return (
    <Layout>
      <Head>
        <title>The Earth Prize - Copyright Policy</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta
          property="og:title"
          content="The Earth Prize - Copyright Policy"
        />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header />
      <CopyrightPolicyWrapper>
        <H1>COPYRIGHT POLICY</H1>
        <Text>
          <b>Effective from:</b> 1st February, 2024
        </Text>
        <br />
        <Text>
          This The Earth Prize Copyright Policy is a part of our{' '}
          <span
            onClick={() => onRoute('/terms-and-conditions')}
            aria-hidden="true"
          >
            Terms & Conditions
          </span>
          . It explains the rules applicable to the content available of this
          website and submitted by you through the website.
        </Text>
        <br />
        <a href="#YOUR-CONTENT">
          <H4>1. YOUR CONTENT</H4>
        </a>
        <a href="#OUR-CONTENT">
          <H4>2. OUR CONTENT</H4>
        </a>
        <a href="#THIRD-PARTY">
          <H4>3. THIRD-PARTY CONTENT</H4>
        </a>
        <a href="#FAIR-USE">
          <H4>4. FAIR USE</H4>
        </a>
        <a href="#COPYRIGHT">
          <H4>5. COPYRIGHT INFRINGEMENT</H4>
        </a>
        <a href="#HOW-TO-CONTACT">
          <H4>6. HOW TO CONTACT US?</H4>
        </a>
        <br />
        <H2>
          <span id="YOUR-CONTENT" />
          1. YOUR CONTENT
        </H2>
        <Text>
          You can submit various content through this website, including your
          project for The Earth Prize competition, reviews, comments, files,
          creations, inventions, ideas, and other materials. We refer to such
          content as “Your Content”. You are and remain the owner of Your
          Content. You will retain all intellectual property rights to Your
          Content.
          <br />
          <br />
          You have to make sure that Your Content is unique. Your Content should
          not contain any materials protected by copyright, including music
          tracks, photos, videos, programs, and texts, that you are not allowed
          to use. If Your Content violates our Terms & Conditions or any
          applicable laws, including intellectual property rights of others
          (copyright, trademarks, and alike), you are not allowed to submit such
          content. You will be responsible for paying all royalties, fees, and
          any other monies applicable to Your Content.
          <br />
          <br />
          In order to ensure the security of the website, we may, from time to
          time, review Your Content. If we find that Your Content violates Terms
          & Conditions or may harm our reputation, we will refuse to upload,
          modify, delete, or remove Your Content, in whole or in part. However,
          you remain solely responsible for Your Content.
          <br />
          <br />
          When participating in The Earth Prize competition, you must not
          appropriate ideas of other Participants and agree to maintain the
          confidentiality of other Participants’ ideas, creations, inventions,
          and other materials.
        </Text>
        <br />
        <H2>
          <span id="OUR-CONTENT" />
          2. OUR CONTENT
        </H2>
        <Text>
          We own or are authorized to use most of the content that you can find
          on the Website (excluding Your Content). We refer to such content as
          “Our Content”. Our Content includes, but is not limited to, text,
          images, audiovisual content, source code, trademarks, service marks,
          and trade names. Our Content is protected by the applicable
          intellectual property laws and international treaties.
          <br />
          <br />
          You are not allowed, without asking our prior permission, to copy,
          distribute, make available, disassemble, make alterations, decompile,
          reverse engineer, translate, adapt, rent, loan, use, lease, or attempt
          to grant other rights to Our Content to third parties, or use any
          manual or automated means to scrape any content available on the
          website.
        </Text>
        <br />
        <H2>
          <span id="THIRD-PARTY" />
          3. THIRD-PARTY CONTENT
        </H2>
        <Text>
          Your Content is owned by you; Our Content - by us. On the website, you
          may also find content that is owned by third parties (for example,
          trademarks). Such third-party intellectual property does not belong to
          us or to you, and it remains the sole property of the respective
          third-party proprietor. If you decide to use the third-party content
          that you can find on the website, you must ask permission from its
          owner beforehand.
        </Text>
        <br />
        <H2>
          <span id="FAIR-USE" />
          4. FAIR USE
        </H2>
        <Text>
          In the US and other countries, it may be possible to reuse materials
          protected by copyright without obtaining permission from a copyright
          holder under certain conditions (this is called ‘fair use’). Such fair
          use is determined on a case-by-case basis and different rules may
          apply in different countries.
          <br />
          <br />
          For example, if a work protected by copyright is used for teaching,
          commentary, criticism, news, or research purposes, such a work may
          fall within the scope of fair use and, therefore, you may not need
          authorization to use such works.
          <br />
          <br />
          We cannot tell you whether your use of protected works falls within
          the scope of the fair use doctrine. Thus, we strongly recommend
          consulting a qualified lawyer and make a proper prior assessment of
          your materials that you intend to use under the fair use doctrine.
        </Text>
        <br />
        <H2>
          <span id="COPYRIGHT-INFRINGEMENT" />
          5. COPYRIGHT INFRINGEMENT
        </H2>
        <Text>
          Intellectual property rights must be respected, and we do so. If you
          believe that our website contains materials that belong to you or
          someone else, please immediately contact us by email.
          <br />
          <br />
          In order to write a proper copyright infringement notice, please
          perform the following steps:
        </Text>
        <br />
        <ul>
          <li>
            Identify with sufficient detail the protected work that you believe
            has been infringed;
          </li>
          <li>
            Identify the material that, in your opinion, is infringing and
            provide sufficient information that permits us to locate the
            material;
          </li>
          <li>
            Provide us with your contact details or the contact details of the
            person that submits the complaint (address, telephone number, and
            email address);
          </li>
          <li>
            Add the following statement:{' '}
            <i>
              “I have a good faith belief that use of the material in the manner
              complained of is not authorized by the copyright owner, its agent,
              or the law”
            </i>
            ;
          </li>
          <li>
            Add the following statement:{' '}
            <i>
              “I swear, under penalty of perjury, that the information in the
              notification is accurate, and that I am the copyright owner or am
              authorized to act on behalf of the owner of an exclusive right
              that is allegedly infringed”
            </i>
            ;
          </li>
          <li>Sign the document; and</li>
          <li>
            Send the document to us by email (info@theearthprize.org) or post:
            The Earth Foundation, Boulevard du théâtre 5, 1204 Genève,
            Switzerland.
          </li>
        </ul>
        <br />
        <Text>
          Please note that you may be liable for damages, including costs and
          attorney’s fees, if you materially misrepresent that material is
          infringing your copyright(s). Therefore, if you are not sure if you
          are the proper copyright holder or if copyright laws protect the
          material of yours, you may need to consult a lawyer.
        </Text>
        <br />
        <H2>
          <span id="HOW-TO-CONTACT" />
          6. HOW TO CONTACT US?
        </H2>
        <Text>
          If you have any questions about this copyright policy, please contact
          us by using the following contact details:
        </Text>
        <br />
        <AddressWrapper>
          <div>
            <Text>
              <b>Email:</b>
              <br />
              <b>Postal address:</b>
            </Text>
          </div>
          <div>
            <Text>
              info@theearthprize.org
              <br />
              The Earth Foundation
              <br />
              Boulevard du théâtre 5
              <br />
              1204 Genève
              <br />
              Switzerland
            </Text>
          </div>
        </AddressWrapper>
      </CopyrightPolicyWrapper>
      <Footer fullHeight />
    </Layout>
  );
};

const CopyrightPolicyWrapper = styled(LayoutWrapper)`
  ${({ theme: { Colors, Consts } }) => css`
    padding: 246px 80px 142px;
    background-color: ${Colors.CopyrightPolicyWrapperBackgroundColor};
    @media (max-width: ${Consts.tabletBreakPoint}) {
      padding: 100px 30px 121px;
    }

    h1 {
      margin-bottom: 34px;
      color: ${Colors.CopyrightPolicyWrapperColor};
      @media (max-width: ${Consts.tabletBreakPoint}) {
        margin-bottom: 20px;
        font-size: 31px;
      }
    }

    h2 {
      margin-bottom: 34px;
      font-size: 28px;
      color: ${Colors.CopyrightPolicyWrapperColor};

      > span {
        position: relative;
        top: -100px;
      }

      @media (max-width: ${Consts.tabletBreakPoint}) {
        margin-bottom: 20px;
        font-size: 20px;
      }
    }

    > a {
      text-decoration: underline;
      color: ${Colors.CopyrightPolicyWrapperColor};
      h4 {
        font-size: 18px;
        color: ${Colors.CopyrightPolicyWrapperColor};
      }
    }

    p {
      margin-bottom: 0;
      color: ${Colors.CopyrightPolicyWrapperColor};

      > span {
        text-decoration: underline;
        cursor: pointer;
      }
    }

    ul {
      padding-left: 30px;
      font-size: 14px;

      > li {
        color: ${Colors.CopyrightPolicyWrapperColor};
        font-size: 18px;
        margin-bottom: 10px;

        @media (max-width: ${Consts.tabletBreakPoint}) {
          font-size: 14px;
        }

        > span {
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  `}
`;

const AddressWrapper = styled.div`
  display: flex;
  align-items: flex-start;

  > div {
    margin-right: 20px;
  }
`;

export default CopyrightPolicy;
